import React, { useEffect, useState } from 'react';
import { Button, Input, Alert, Space, Typography, message } from 'antd';
import { useTranslation } from '../../shared/i18n';
import transferService from '../services/transferService';

const { Text } = Typography;

/**
 * 转移表单：填对方账号 → 确认打码名 + 阻断/提醒 → 二次验证 → 发起（对方接受后生效）。
 * 只在「资源转移」页的新建流程里用（资源详情页不再放转移按钮）。
 * props: type (evm|domain|cdn|dns|host_shared|host), id, name, onBack(返回上一步), onDone(发起成功)
 */
const TransferForm = ({ type, id, name, onBack, onDone }) => {
    const { t } = useTranslation();
    const [recipient, setRecipient] = useState('');
    const [preview, setPreview] = useState(null);
    const [method, setMethod] = useState('password');
    const [secret, setSecret] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        transferService.verifyMethod().then((d) => setMethod(d?.method || 'password')).catch(() => {});
    }, []);

    const next = async () => {
        if (!recipient.trim()) return;
        setLoading(true); setError('');
        try {
            setPreview(await transferService.preview(type, id, recipient.trim()));
        } catch (e) {
            setError(t(e.error || 'Operation failed'));
        } finally {
            setLoading(false);
        }
    };

    const submit = async () => {
        if (!secret.trim()) return;
        setLoading(true); setError('');
        try {
            await transferService.create({
                type, id, recipient: recipient.trim(),
                ...(method === 'totp' ? { code: secret.trim() } : { password: secret }),
            });
            message.success(t('Transfer started; waiting for the recipient to accept'));
            onDone?.();
        } catch (e) {
            setError(t(e.error || 'Operation failed'));
        } finally {
            setLoading(false);
        }
    };

    const back = () => {
        if (preview) { setPreview(null); setSecret(''); setError(''); } else onBack?.();
    };
    const blocked = !!preview?.blocker;

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text strong>{name}</Text>
            {!preview ? (
                <Input
                    autoFocus
                    placeholder={t("Recipient's username or email")}
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    onPressEnter={next}
                />
            ) : (
                <>
                    <div>{t('Transfer to')} <Text strong>{preview.recipient}</Text></div>
                    {blocked && <Alert type="error" showIcon message={t(preview.blocker)} />}
                    {!blocked && (preview.warnings || []).length > 0 && (
                        <Alert type="warning" showIcon message={(
                            <div>{preview.warnings.map((w) => <div key={w}>{t(w)}</div>)}</div>
                        )} />
                    )}
                    {!blocked && (method === 'totp' ? (
                        <Input autoFocus placeholder={t('Two-factor code')} value={secret}
                            onChange={(e) => setSecret(e.target.value)} onPressEnter={submit} />
                    ) : (
                        <Input.Password autoFocus placeholder={t('Account password')} value={secret}
                            onChange={(e) => setSecret(e.target.value)} onPressEnter={submit} />
                    ))}
                    {!blocked && <Text type="secondary" style={{ fontSize: 12 }}>{t('Takes effect when the recipient accepts within 72 hours; renewals move to them')}</Text>}
                </>
            )}
            {error && <Alert type="error" showIcon message={error} />}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                {(preview || onBack) && <Button onClick={back}>{t('Previous step')}</Button>}
                {preview ? (
                    <Button type="primary" loading={loading} disabled={blocked || !secret.trim()} onClick={submit}>{t('Start transfer')}</Button>
                ) : (
                    <Button type="primary" loading={loading} disabled={!recipient.trim()} onClick={next}>{t('Next step')}</Button>
                )}
            </div>
        </Space>
    );
};

export default TransferForm;
